import React, { useState, useRef } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import Draggable from "react-draggable";
import styles from "./PdfSelloViewer.module.css";
import { getSellosGuardados } from "./SelloManager";

// Worker de PDF.js
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const PdfSelloViewer = ({ pdfFile, onConfirm, onCancel }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [selloImg, setSelloImg] = useState(null);
  const [selloPos, setSelloPos] = useState({ x: 100, y: 100 });
  const [selloSize, setSelloSize] = useState({ width: 140, height: 70 });
  const [aspectRatio, setAspectRatio] = useState(2);
  const [isDragging, setIsDragging] = useState(false);
  const [activeTab, setActiveTab] = useState("guardados"); // "guardados" | "nuevo"
  const [sellosGuardados] = useState(getSellosGuardados);

  const pageRef = useRef(null);
  const selloInputRef = useRef(null);
  const draggableRef = useRef(null);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const handleSelectSello = (base64Str) => {
    setSelloImg(base64Str);
    const img = new Image();
    img.onload = () => {
      const ratio = img.width / img.height;
      setAspectRatio(ratio);
      setSelloSize({ width: 140, height: Math.round(140 / ratio) });
    };
    img.src = base64Str;
  };

  const handleSelloUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const MAX_SIZE = 800;
        let w = img.width;
        let h = img.height;
        
        if (w > MAX_SIZE || h > MAX_SIZE) {
          if (w > h) {
            h = Math.round((h * MAX_SIZE) / w);
            w = MAX_SIZE;
          } else {
            w = Math.round((w * MAX_SIZE) / h);
            h = MAX_SIZE;
          }
        }

        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        
        const base64Str = canvas.toDataURL("image/png");
        handleSelectSello(base64Str);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleDragStop = (e, data) => {
    setSelloPos({ x: data.x, y: data.y });
    setIsDragging(false);
  };

  const handleConfirm = () => {
    if (!selloImg) {
      alert("Por favor carga una imagen de sello o firma primero.");
      return;
    }
    const pageEl = pageRef.current;
    if (!pageEl) return;

    const pageRect = pageEl.getBoundingClientRect();

    // Normalizar coordenadas relativas al tamaño real del PDF renderizado
    const coords = {
      page: pageNumber,
      x: selloPos.x,
      y: selloPos.y,
      width: selloSize.width,
      height: selloSize.height,
      pageWidth: pageRect.width,
      pageHeight: pageRect.height,
      selloBase64: selloImg,
    };

    onConfirm(coords);
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        {/* HEADER */}
        <div className={styles.header}>
          <h2 className={styles.headerTitle}>
            📄 Posicionar Sello / Firma
          </h2>
          <button className={styles.closeBtn} onClick={onCancel}>✕</button>
        </div>

        <div className={styles.body}>
          {/* PANEL IZQUIERDO: controles */}
          <div className={styles.sidebar}>

            {/* TABS */}
            <div className={styles.tabs}>
              <button
                className={`${styles.tab} ${activeTab === "guardados" ? styles.tabActive : ""}`}
                onClick={() => setActiveTab("guardados")}
              >
                🗂️ Mis Sellos
              </button>
              <button
                className={`${styles.tab} ${activeTab === "nuevo" ? styles.tabActive : ""}`}
                onClick={() => setActiveTab("nuevo")}
              >
                📎 Subir nuevo
              </button>
            </div>

            {/* TAB: SELLOS GUARDADOS */}
            {activeTab === "guardados" && (
              <div className={styles.sideSection}>
                {sellosGuardados.length === 0 ? (
                  <div className={styles.emptySeals}>
                    <span>📭</span>
                    <p>No tienes sellos guardados. Ve a "Subir nuevo" o adminístralos desde el botón de gestión.</p>
                  </div>
                ) : (
                  <div className={styles.selloGrid}>
                    {sellosGuardados.map((s) => (
                      <div
                        key={s.id}
                        className={`${styles.selloThumb} ${selloImg === s.base64 ? styles.selloThumbActive : ""}`}
                        onClick={() => handleSelectSello(s.base64)}
                      >
                        <img src={s.base64} alt={s.nombre} className={styles.selloThumbImg} />
                        <p className={styles.selloThumbName}>{s.nombre}</p>
                      </div>
                    ))}
                  </div>
                )}
                {selloImg && (
                  <p className={styles.selectedHint}>✅ Sello seleccionado — arrástralo sobre el PDF</p>
                )}
              </div>
            )}

            {/* TAB: SUBIR NUEVO */}
            {activeTab === "nuevo" && (
              <div className={styles.sideSection}>
                <p className={styles.sideLabel}>Subir imagen puntual</p>
                <p className={styles.sideHint}>
                  Esta imagen se usará solo para esta firma.<br/>
                  Para guardarla, usa el botón "Gestionar sellos" en la pantalla principal.
                </p>
                <button
                  className={styles.uploadBtn}
                  onClick={() => selloInputRef.current?.click()}
                >
                  📎 Seleccionar imagen
                </button>
                <input
                  ref={selloInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleSelloUpload}
                />
                {selloImg && (
                  <div className={styles.previewBox}>
                    <img src={selloImg} alt="Sello" className={styles.previewImg} />
                    <p className={styles.previewLabel}>✅ Cargado</p>
                  </div>
                )}
              </div>
            )}

            {/* TAMAÑO (Mantiene proporción) */}
            <div className={styles.sideSection}>
              <p className={styles.sideLabel}>Ajusta el tamaño</p>
              <label className={styles.sliderLabel}>
                Escala: <strong>{selloSize.width}px</strong> de ancho
              </label>
              <input
                type="range"
                min={40}
                max={400}
                value={selloSize.width}
                onChange={(e) => {
                  const w = +e.target.value;
                  setSelloSize({ width: w, height: Math.round(w / aspectRatio) });
                }}
                className={styles.slider}
              />
            </div>

            <div className={styles.sideSection}>
              <p className={styles.sideLabel}>3. Página del documento</p>
              <div className={styles.pageControls}>
                <button
                  className={styles.pageBtn}
                  onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                  disabled={pageNumber <= 1}
                >
                  ◀
                </button>
                <span className={styles.pageIndicator}>
                  {pageNumber} / {numPages || "—"}
                </span>
                <button
                  className={styles.pageBtn}
                  onClick={() => setPageNumber((p) => Math.min(numPages, p + 1))}
                  disabled={pageNumber >= numPages}
                >
                  ▶
                </button>
              </div>
            </div>

            <div className={styles.sideSection}>
              <p className={styles.sideHint}>
                4. Arrastra el sello sobre el documento al lugar que desees.
              </p>
            </div>

            <div className={styles.actions}>
              <button
                className={styles.confirmBtn}
                onClick={handleConfirm}
                disabled={!selloImg}
              >
                ✅ Confirmar y Firmar
              </button>
              <button className={styles.cancelBtn} onClick={onCancel}>
                Cancelar
              </button>
            </div>
          </div>

          {/* PANEL DERECHO: visor del PDF */}
          <div className={styles.pdfArea}>
            <div className={styles.pdfWrapper} ref={pageRef}>
              <Document
                file={pdfFile}
                onLoadSuccess={onDocumentLoadSuccess}
                loading={
                  <div className={styles.loading}>Cargando PDF...</div>
                }
                error={
                  <div className={styles.loadingError}>
                    No se pudo cargar el PDF.
                  </div>
                }
              >
                <Page
                  pageNumber={pageNumber}
                  width={620}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </Document>

              {/* SELLO ARRASTRABLE */}
              {selloImg && (
                <Draggable
                  nodeRef={draggableRef}
                  bounds="parent"
                  position={selloPos}
                  onStart={() => setIsDragging(true)}
                  onStop={handleDragStop}
                >
                  <div
                    ref={draggableRef}
                    className={`${styles.selloDraggable} ${
                      isDragging ? styles.dragging : ""
                    }`}
                    style={{
                      width: selloSize.width,
                      height: selloSize.height,
                      position: "absolute",
                      top: 0,
                      left: 0,
                      cursor: isDragging ? "grabbing" : "grab",
                    }}
                  >
                    <img
                      src={selloImg}
                      alt="Sello"
                      style={{ width: "100%", height: "100%", objectFit: "contain" }}
                      draggable={false}
                    />
                    <div className={styles.selloHint}>✋ Arrastra</div>
                  </div>
                </Draggable>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PdfSelloViewer;
